import { useState, useEffect, useCallback } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, Settings2, Target, Info } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { SetupCompletenessBanner } from "@/components/setup-completeness-banner";
import { AccountSetupPending, useStarterSeedPending } from "@/components/account-setup-pending";
import { BUY_BOX_REQUIRED_CRITERIA_FIELDS, getMissingBuyBoxFields } from "@shared/buy-box-setup";
import { BuyBoxSubnav } from "@/components/buy-box/buy-box-subnav";

function CriteriaField({ label, value, onChange, onBlur, prefix, suffix, testId, placeholder }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  prefix?: string;
  suffix?: string;
  testId: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{prefix}</span>}
        <Input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          className={`text-sm ${prefix ? "pl-6" : ""} ${suffix ? "pr-8" : ""}`}
          placeholder={placeholder || "Leave blank to skip"}
          data-testid={testId}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
}

export default function AppBuyBoxSettings() {
  const queryClient = useQueryClient();
  usePageTitle("Buy Box Settings");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("underwriting");
  const { toast } = useToast();
  const [fields, setFields] = useState<Record<string, string>>({});

  const seedingPending = useStarterSeedPending();

  const criteriaQuery = useQuery<Record<string, unknown>>({
    queryKey: ["/api/app/buy-box/criteria"],
    // While a newly-approved host's seeds are still in flight, poll so the
    // criteria appear the moment they're written instead of an empty form.
    refetchInterval: (query) => {
      const data = query.state.data as Record<string, unknown> | undefined;
      const hasRow = data?.id != null;
      return seedingPending && !hasRow ? 2500 : false;
    },
  });

  const defaultsSummaryQuery = useQuery<Record<string, unknown>>({
    queryKey: ["/api/app/buy-box/defaults-summary"],
  });

  const hasCriteriaRow = (criteriaQuery.data as Record<string, unknown> | undefined)?.id != null;
  const showSetupPending = seedingPending && !criteriaQuery.isLoading && !hasCriteriaRow;

  useEffect(() => {
    if (criteriaQuery.data) {
      const d = criteriaQuery.data as Record<string, unknown>;
      setFields({
        minCocReturn: d.minCocReturn != null ? String(d.minCocReturn) : "",
        minMonthlyCashFlow: d.minMonthlyCashFlow != null ? String(d.minMonthlyCashFlow) : "",
        maxPurchasePrice: d.maxPurchasePrice != null ? String(d.maxPurchasePrice) : "",
        minBedrooms: d.minBedrooms != null ? String(d.minBedrooms) : "",
        maxBedrooms: d.maxBedrooms != null ? String(d.maxBedrooms) : "",
        minSqft: d.minSqft != null ? String(d.minSqft) : "",
        maxSqft: d.maxSqft != null ? String(d.maxSqft) : "",
        noHoa: d.noHoa ? "true" : "false",
      });
    }
  }, [criteriaQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiRequest("PUT", "/api/app/buy-box/criteria", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/criteria"] });
      toast({ title: "Saved" });
    },
    onError: () => {
      toast({ title: "Failed to save", variant: "destructive" });
    },
  });

  const setField = useCallback((field: string, val: string) => {
    setFields(prev => ({ ...prev, [field]: val }));
  }, []);

  const saveField = useCallback((field: string) => {
    const val = fields[field];
    const numVal = val === "" ? null : parseFloat(val);
    if (val !== "" && isNaN(numVal as number)) return;
    saveMutation.mutate({ [field]: numVal });
  }, [fields, saveMutation]);

  const defaults = defaultsSummaryQuery.data as Record<string, unknown> | undefined;
  const defaultsExist = defaults?.exists === true;

  if (criteriaQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl">
        <ViewOnlyBanner area="underwriting" />
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Target className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900" data-tour="buy-box-settings-heading">Buy Box Settings</h1>
          </div>
          <ExplainerVideoButton pageKey="buy-box-settings" />
          <GuideButton pageKey="buy-box-settings" />
        </div>
        <BuyBoxSubnav />
        <GuideNudgeBanner pageKey="buy-box-settings" />

        <p className="text-sm text-gray-500 mb-6">
          Define your investment criteria. Properties found during a city scan must meet these thresholds to be marked as passing.
          Every field below is required before you can run a scan.
        </p>

        {showSetupPending ? (
          <AccountSetupPending label="Buy Box criteria" />
        ) : (
        <>
        <SetupCompletenessBanner
          missing={getMissingBuyBoxFields(criteriaQuery.data)}
          total={BUY_BOX_REQUIRED_CRITERIA_FIELDS.length}
          readyLabel="All criteria set — ready for scanning"
          testIdPrefix="buy-box-setup"
        />

        <fieldset disabled={readOnly} style={{ display: "contents" }}>
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6" data-tour="buy-box-settings-criteria">
          <h2 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide text-gray-500">Investment Criteria</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CriteriaField
              label="Min Cash-on-Cash Return (%)"
              value={fields.minCocReturn || ""}
              onChange={v => setField("minCocReturn", v)}
              onBlur={() => saveField("minCocReturn")}
              suffix="%"
              testId="input-min-coc-return"
              placeholder="e.g. 8"
            />
            <CriteriaField
              label="Min Monthly Cash Flow ($)"
              value={fields.minMonthlyCashFlow || ""}
              onChange={v => setField("minMonthlyCashFlow", v)}
              onBlur={() => saveField("minMonthlyCashFlow")}
              prefix="$"
              testId="input-min-monthly-cash-flow"
              placeholder="e.g. 300"
            />
            <CriteriaField
              label="Max Purchase Price ($)"
              value={fields.maxPurchasePrice || ""}
              onChange={v => setField("maxPurchasePrice", v)}
              onBlur={() => saveField("maxPurchasePrice")}
              prefix="$"
              testId="input-max-purchase-price"
              placeholder="e.g. 300000"
            />
            <CriteriaField
              label="Min Converted Bedrooms"
              value={fields.minBedrooms || ""}
              onChange={v => setField("minBedrooms", v)}
              onBlur={() => saveField("minBedrooms")}
              testId="input-min-bedrooms"
              placeholder="e.g. 3"
            />
            <CriteriaField
              label="Max Converted Bedrooms"
              value={fields.maxBedrooms || ""}
              onChange={v => setField("maxBedrooms", v)}
              onBlur={() => saveField("maxBedrooms")}
              testId="input-max-bedrooms"
              placeholder="e.g. 7"
            />
            <CriteriaField
              label="Min Square Footage"
              value={fields.minSqft || ""}
              onChange={v => setField("minSqft", v)}
              onBlur={() => saveField("minSqft")}
              suffix="sqft"
              testId="input-min-sqft"
              placeholder="e.g. 1200"
            />
            <CriteriaField
              label="Max Square Footage"
              value={fields.maxSqft || ""}
              onChange={v => setField("maxSqft", v)}
              onBlur={() => saveField("maxSqft")}
              suffix="sqft"
              testId="input-max-sqft"
              placeholder="e.g. 3000"
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Checkbox
              id="noHoa"
              checked={fields.noHoa === "true"}
              onCheckedChange={(checked) => {
                const val = checked === true;
                setFields(prev => ({ ...prev, noHoa: val ? "true" : "false" }));
                saveMutation.mutate({ noHoa: val });
              }}
              data-testid="checkbox-no-hoa"
            />
            <label htmlFor="noHoa" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
              No HOA — Exclude properties with HOA fees from scan results
            </label>
          </div>
          <div className="mt-4 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
            <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700">
              Bedroom counts are automatically converted from total convertible area using the formula: <strong>(living sqft + garage sqft) ÷ 250</strong> (rounded). Garage area is included because it can be converted to bedrooms. Bathrooms are calculated as 1 per 3 bedrooms.
            </p>
          </div>
        </div>
        </fieldset>
        </>
        )}

        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex items-center justify-between gap-3" data-testid="card-buy-box-agents-link">
          <p className="text-sm text-gray-600">
            Buy Box Agents now live on their own tab. These settings are <strong>Buy Box V1</strong> there; save other versions for different agents.
          </p>
          <Link href="/app/buy-box/agents">
            <span className="shrink-0 text-sm font-medium text-blue-600 hover:underline cursor-pointer" data-testid="link-buy-box-agents">Manage agents</span>
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6" data-tour="buy-box-settings-markets">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-gray-500" />
              <h2 className="font-semibold text-gray-900 text-sm uppercase tracking-wide text-gray-500">Underwriting Defaults</h2>
            </div>
            <Link href="/app/underwriting/defaults">
              <span className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 cursor-pointer" data-testid="link-edit-defaults">
                Edit Defaults <ExternalLink className="h-3 w-3" />
              </span>
            </Link>
          </div>
          {defaultsSummaryQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !defaultsExist ? (
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
              No underwriting defaults set yet.{" "}
              <Link href="/app/underwriting/defaults">
                <span className="text-blue-600 hover:underline cursor-pointer">Set your defaults</span>
              </Link>{" "}
              to improve Buy Box accuracy.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                { label: "Down Payment", value: defaults?.downpaymentPercent, suffix: "%" },
                { label: "Closing Costs", value: defaults?.closingCostsPercent, suffix: "%" },
                { label: "Interest Rate", value: defaults?.annualInterestRate, suffix: "%" },
                { label: "Vacancy", value: defaults?.vacancyPercent, suffix: "%" },
                { label: "PM Fee", value: defaults?.pmFee, suffix: defaults?.pmFeeType === "fixed" ? "/mo" : "%" },
                { label: "Property Tax", value: defaults?.annualPropertyTaxPercent, suffix: "%" },
                { label: "Insurance", value: defaults?.annualInsurancePercent, suffix: "%" },
                { label: "Furnishing/Room", value: defaults?.furnishingCostPerRoom, prefix: "$" },
                { label: "Electricity/Room", value: defaults?.electricityPerRoom, prefix: "$" },
                { label: "Room Addition Cost", value: defaults?.roomAdditionsCost, prefix: "$" },
                { label: "Bath Addition Cost", value: defaults?.bathAdditionsCost, prefix: "$" },
                { label: "Additional Rehab", value: defaults?.additionalRehabAmount, suffix: "% of PP" },
                { label: "CapEx Buffer", value: defaults?.bufferPercent, suffix: "%" },
                { label: "Months Until Occupied", value: defaults?.monthsUntilOccupied, suffix: "mo" },
              ].map(item => (
                <div key={item.label} className="bg-gray-50 rounded-lg p-2.5">
                  <div className="text-xs text-gray-500 mb-0.5">{item.label}</div>
                  <div className="font-medium text-gray-800">
                    {item.prefix}{item.value != null && item.value !== "" ? String(item.value) : "—"}{item.suffix && item.value != null && item.value !== "" ? ` ${item.suffix}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            These defaults are used in underwriting calculations during Buy Box scans. Edit them from the Underwriting Defaults page.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
